import React, { useState, useEffect } from 'react';
import ModalSymphony from 'elements/ModalSymphony';
import InputSymphony, { SelectOptionItem } from 'elements/InputSymphony';
import { contrastLinkImgMapping, streamingLinkNameMapping, filterOutLinks } from 'helpers/StreamingMappings';

export interface CustomLink {
  id: string;
  type: 'custom';
  title: string;
  url: string;
  icon?: string; // Platform ID (e.g., 'spotify', 'apple_music') if selected
}

interface AddEditCustomLinkModalProps {
  open: boolean;
  onClose: () => void;
  onSave: (link: CustomLink) => void;
  editingLink?: CustomLink | null;
  existingTitles?: string[];
}

// Get all available streaming service icons from the existing mappings
const getAllStreamingServiceOptions = (): SelectOptionItem[] => {
  const options: SelectOptionItem[] = [
    { value: 'none', title: 'No Icon' }
  ];

  // Use the existing streamingLinkNameMapping instead of hardcoding
  Object.entries(streamingLinkNameMapping).forEach(([serviceId, serviceName]) => {
    // Filter out unwanted services using the existing filterOutLinks function
    if (filterOutLinks(serviceId)) {
      const iconPath = contrastLinkImgMapping('', 'dark', serviceId);
      if (iconPath && iconPath.default) {
        options.push({
          value: serviceId,
          title: serviceName
          // Removed thumbnailUrl to not show icon preview in dropdown
        });
      }
    }
  });

  return options;
};

const AddEditCustomLinkModal: React.FC<AddEditCustomLinkModalProps> = ({
  open,
  onClose,
  onSave,
  editingLink = null,
  existingTitles = []
}) => {
  const [title, setTitle] = useState('');
  const [url, setUrl] = useState('');
  const [selectedIcon, setSelectedIcon] = useState('none');
  const [titleError, setTitleError] = useState('');
  const [urlError, setUrlError] = useState('');
  const [loading, setLoading] = useState(false);

  const isEditing = Boolean(editingLink);
  const iconOptions = getAllStreamingServiceOptions();

  useEffect(() => {
    if (open) {
      if (editingLink) {
        console.log('editing link:', editingLink);
        setTitle(editingLink.title);
        setUrl(editingLink.url);
        // The icon field now stores the platform ID directly
        console.log('editingLink.icon:', editingLink.icon);
        setSelectedIcon(editingLink.icon || 'none');
      } else {
        console.log('setting title, url, and icon to none');
        setTitle('');
        setUrl('');
        setSelectedIcon('none');
      }
      setTitleError('');
      setUrlError('');
    }
  }, [open, editingLink]);

  const validateTitle = (value: string): string => {
    if (!value.trim()) {
      return 'Title is required';
    }
    if (value.length > 50) {
      return 'Title must be 50 characters or less';
    }
    // Check for duplicate titles (excluding current editing link)
    const duplicateExists = existingTitles.some(existingTitle => 
      existingTitle.toLowerCase() === value.trim().toLowerCase() && 
      (!editingLink || existingTitle !== editingLink.title)
    );
    if (duplicateExists) {
      return 'A custom link with this title already exists';
    }
    return '';
  };

  const validateUrl = (value: string): string => {
    if (!value.trim()) {
      return 'URL is required';
    }
    
    // Basic URL validation
    try {
      const urlObj = new URL(value);
      // Ensure it's http or https
      if (!['http:', 'https:'].includes(urlObj.protocol)) {
        return 'URL must use http or https protocol';
      }
      return '';
    } catch {
      return 'Please enter a valid URL (e.g., https://example.com)';
    }
  };

  const handleTitleChange = (value: string) => {
    console.log('handleTitleChange', value);
    setTitle(value);
    // Clear error when user starts typing
    if (titleError) {
      setTitleError('');
    }
  };

  const handleUrlChange = (value: string) => {
    setUrl(value);
    // Clear error when user starts typing
    if (urlError) {
      setUrlError('');
    }
  };

  const handleIconSelect = (option: SelectOptionItem) => {
    setSelectedIcon(option.value);
  };

  const handleSave = () => {
    const titleValidationError = validateTitle(title);
    const urlValidationError = validateUrl(url);

    setTitleError(titleValidationError);
    setUrlError(urlValidationError);

    if (titleValidationError || urlValidationError) {
      return;
    }

    setLoading(true);

    // Store the platform ID instead of the actual image path
    // This allows us to apply light/dark mode logic later
    console.log('selectedIcon:', selectedIcon);
    const iconPlatformId = selectedIcon !== 'none' ? selectedIcon : undefined;

    const linkToSave: CustomLink = {
      id: editingLink?.id || `custom-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'custom',
      title: title.trim(),
      url: url.trim(),
      ...(iconPlatformId && { icon: iconPlatformId })
    };

    console.log('linkToSave:', linkToSave);

    // Simulate async operation
    setTimeout(() => {
      onSave(linkToSave);
      setLoading(false);
      onClose();
    }, 100);
  };

  const handleClose = () => {
    if (!loading) {
      onClose();
    }
  };

  const selectedIconOption = iconOptions.find(option => option.value === selectedIcon);

  return (
    <ModalSymphony
      className="addEditCustomLinkModal"
      isOpen={open}
      onClose={handleClose}
      title={isEditing ? 'Edit Custom Link' : 'Add Custom Link'}
      subtitle={isEditing ? 'Update your custom link details' : 'Add a custom link to your pre-save page'}
      buttonLeftText="Cancel"
      onClickButtonLeft={handleClose}
      buttonLeftDisabled={loading}
      buttonRightText={isEditing ? 'Update Link' : 'Add Link'}
      onClickButtonRight={handleSave}
      buttonRightIsLoading={loading}
      buttonRightDisabled={loading}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
        <InputSymphony
          className="customLinkTitleInput"
          label="Link Title"
          placeholder="e.g., Buy on iTunes, Download MP3"
          value={title}
          onChange={handleTitleChange}
          error={titleError}
          helperText={titleError || 'This will be displayed as the button text'}
          disabled={loading}
        />

        <InputSymphony
          className="customLinkUrlInput"
          label="URL"
          placeholder="https://music.apple.com/album/..."
          value={url}
          onChange={handleUrlChange}
          error={urlError}
          helperText={urlError || 'The link where users will be directed'}
          disabled={loading}
        />

        <InputSymphony
          className="customLinkIconSelect"
          label="Use Streaming Service Icon (Optional)"
          placeholder="Choose an icon..."
          value={selectedIcon}
          isSelectDropdown={true}
          options={iconOptions}
          onOptionSelect={handleIconSelect}
          disabled={loading}
          helperText="Choose an icon from our streaming platforms"
        />
      </div>
    </ModalSymphony>
  );
};

export default AddEditCustomLinkModal; 